/** Pure presentation helpers for the Requests activity-log dashboard page. */

import type { RequestLevel } from '../features/requests/model';
import { m } from '../paraglide/messages';
import { DAY_STAMP, fmtInstant } from './format';

/* ---- time presets ---- */

/** `label` is the message itself, called at render so it reads the page locale. */
export const TIME_PRESETS = [
  { id: '1h', label: m.usage_preset_1h, hours: 1 },
  { id: '12h', label: m.usage_preset_12h, hours: 12 },
  { id: '24h', label: m.usage_preset_24h, hours: 24 },
  { id: '7d', label: m.usage_preset_7d, hours: 24 * 7 },
  { id: '30d', label: m.usage_preset_30d, hours: 24 * 30 },
];

export const DEFAULT_PRESET_ID = '24h';

/** The `{from,to}` millis window for a preset id, anchored at `now`. Falls
 *  back to the default preset for an unknown id. */
export const presetRange = (id: string, now: number): { from: number; to: number } => {
  const preset = TIME_PRESETS.find((p) => p.id === id) ?? TIME_PRESETS.find((p) => p.id === DEFAULT_PRESET_ID)!;
  return { from: now - preset.hours * 3600_000, to: now };
};

/* ---- pinned calendar day ---- */

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/u;

/** Midnight local to this browser on the given calendar day. */
const localMidnight = (day: string, plusDays = 0): Date | null => {
  const parts = DAY_PATTERN.exec(day);
  if (!parts) return null;
  const [y, m, d] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
  const at = new Date(y, m - 1, d + plusDays);
  // Reject a well-formed-but-impossible date. The Date constructor rolls
  // 2026-02-31 forward to March 3rd, which would pin the log to a day nobody
  // asked for; comparing the components back out is what catches it.
  if (plusDays === 0 && (at.getFullYear() !== y || at.getMonth() !== m - 1 || at.getDate() !== d)) {
    return null;
  }
  return at;
};

/** A `YYYY-MM-DD` string the usage chart can pin the log to, or null. */
export const parseDayParam = (value: unknown): string | null =>
  typeof value === 'string' && localMidnight(value) ? value : null;

/**
 * The `{from,to}` millis window for one pinned calendar day, in the reader's
 * own zone.
 *
 * Local, because the bar that links here is a local day: the gateway buckets
 * the usage chart in the zone the browser reports, so a UTC window here would
 * cover a different set of requests than the bar the reader clicked. Building
 * both ends from calendar components rather than a fixed 24 hours is also what
 * makes a daylight-saving day come out 23 or 25 hours long, as it actually was.
 */
export const dayRange = (day: string): { from: number; to: number } => {
  const from = localMidnight(day)?.getTime() ?? Number.NaN;
  const to = localMidnight(day, 1)?.getTime() ?? Number.NaN;
  return { from, to };
};

/** The pinned day for a human, e.g. `Aug 28, 2026`. No zone suffix: it is the
 *  reader's own day, the same one every timestamp beside it is rendered in. */
export const dayLabel = (day: string): string => {
  const at = localMidnight(day);
  return at ? fmtInstant(at.toISOString(), DAY_STAMP) : day;
};

/* ---- SKU ---- */

/** Splits a SKU slug into its platform prefix and endpoint suffix:
 *  `tiktok.profile` -> `{platform: 'tiktok', endpoint: 'profile'}`. A slug
 *  with no dot has an empty endpoint. */
export const splitSku = (sku: string): { platform: string; endpoint: string } => {
  const i = sku.indexOf('.');
  return i === -1 ? { platform: sku, endpoint: '' } : { platform: sku.slice(0, i), endpoint: sku.slice(i + 1) };
};

/* ---- error copy ---- */

const ERROR_LABELS: Record<string, () => string> = {
  key_cap_exceeded: m.usage_error_key_cap_exceeded,
  upstream_error: m.usage_error_upstream_error,
  timeout: m.usage_error_timeout,
};

/** Human copy for a customer-safe error code. Falls back to underscores-to-
 *  spaces, capitalized, for codes without a specific mapping: that is the
 *  gateway's own code, so it stays in the gateway's words in every locale. */
export const errorLabel = (code: string): string => {
  if (/[^a-z0-9_]/.test(code)) return code;
  const known = Object.hasOwn(ERROR_LABELS, code) ? ERROR_LABELS[code] : undefined;
  if (known) return known();
  const spaced = code.replace(/_/g, ' ');
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : spaced;
};

/* ---- histogram bucket labels ---- */

/** Time-of-day for sub-day windows ("2:00 PM"), or a short date ("Jul 1") once
 *  a bucket spans a full day or more (7d/30d windows). */
export const bucketLabel = (startMs: number, bucketMs: number): string => {
  const iso = new Date(startMs).toISOString();
  if (bucketMs >= 24 * 3600_000) {
    return fmtInstant(iso, { month: 'short', day: 'numeric' });
  }
  return fmtInstant(iso, { hour: 'numeric', minute: bucketMs < 3600_000 ? '2-digit' : undefined });
};

/* ---- badge tone ---- */

/** The `Badge` color for a request level's status badge: success = positive,
 *  error = negative, rejected = amber (warning). Pending has none, so the badge
 *  takes its neutral look. */
export const levelColor = (level: RequestLevel): string | undefined => {
  if (level === 'success') return 'var(--pos)';
  if (level === 'error') return 'var(--neg)';
  if (level === 'rejected') return 'var(--c-amber)';
  return undefined;
};

/** Display label for a request level, title-cased. */
export const levelLabel = (level: RequestLevel): string =>
  level === 'success'
    ? m.usage_level_success()
    : level === 'error'
      ? m.usage_level_error()
      : level === 'rejected'
        ? m.usage_level_rejected()
        : m.usage_level_pending();
